import React, { useState, useEffect } from 'react';
import {
  Card, Form, Row, Col, Button, Alert, Badge, Table, Modal,
} from 'react-bootstrap';
import {
  FaPlus, FaTrash, FaBan, FaInfoCircle,
} from 'react-icons/fa';
import axios from 'axios';

/*
 * UAE PDPL Consent Management — opt-IN register.
 * Federal Decree-Law No. 45 of 2021. Consent must be clear, specific and
 * unambiguous (Art 6), withdrawable at any time, and the controller must be
 * able to prove it. Sensitive personal data requires explicit consent.
 *
 * TODO: replace the PLACEHOLDER endpoints with your real UAE consent API.
 * ACTIVITIES posts to your UAE activity logger (whatever UaeDsarManagementTab uses).
 */
const API = {
  list: 'https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/consent/list',
  create: 'https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/consent/create',
  update: (id) =>
    `https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/consent/update/${id}`,
  remove: (id) =>
    `https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/consent/delete/${id}`,
  activities:
    'https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activities/create',
};

// PDPL Art 4 — consent is one basis; others remove the need for consent.
const LEGAL_BASES = [
  'Consent — Art 4(1)',
  'Contract necessity — Art 4(2)',
  'Legal obligation — Art 4(3)',
  'Protection of public interest — Art 4(4)',
  'Protection of data subject vital interest',
  'Controller legitimate interest',
];

const PURPOSES = [
  'Marketing communications',
  'Profiling & analytics',
  'Service provision',
  'Personalization',
  'Sharing with processors',
  'Cross-border transfer',
];

const COLLECTION_METHODS = [
  'Web form',
  'In-app',
  'Email',
  'Paper form',
  'Recorded verbal',
  'Imported / migrated',
];

const STATUS_VARIANT = {
  Given: 'success',
  Withdrawn: 'secondary',
  Pending: 'warning',
  Expired: 'danger',
};

// inline sub-text style — immune to global CSS that collapses Bootstrap margins
const subTextStyle = {
  fontSize: '12.5px',
  color: '#6c757d',
  marginLeft: '1.6rem',
  marginTop: '2px',
  lineHeight: 1.45,
};

const UaeConsentManagementTab = ({
  userData,
  onScoreUpdate,
  showNotification,
  region,
  refreshActivities,
}) => {
  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'],
  };

  const [records, setRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const blankConsent = {
    dataSubject: '',
    purposes: [],
    legalBasis: 'Consent — Art 4(1)',
    status: 'Given',
    collectionMethod: 'Web form',
    noticeVersion: '',
    sensitiveToggle: false,
    explicitConsent: false,
    guardianToggle: false,
    guardianConsent: false,
    notes: '',
  };
  const [form, setForm] = useState(blankConsent);
  const [errors, setErrors] = useState({});

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const togglePurpose = (p) =>
    setForm((f) => ({
      ...f,
      purposes: f.purposes.includes(p)
        ? f.purposes.filter((x) => x !== p)
        : [...f.purposes, p],
    }));

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logActivity = async (message, badge = 'info') => {
    try {
      await axios.post(
        API.activities,
        { action: badge, module: 'Consent Management', description: message },
        { headers }
      );
      if (refreshActivities) await refreshActivities();
    } catch (err) {
      console.error('❌ Error logging activity:', err);
    }
  };

  const calculateScore = (rows) => {
    if (!rows.length) {
      if (onScoreUpdate) onScoreUpdate(0);
      return;
    }
    const wellFormed = rows.filter(
      (r) =>
        r.legalBasis &&
        r.noticeVersion &&
        Array.isArray(r.purposes) &&
        r.purposes.length > 0 &&
        // sensitive data must carry explicit consent to count as complete
        (!r.sensitive || r.sensitive.explicitConsent)
    ).length;
    const active = rows.filter((r) => r.status === 'Given').length;
    const completeness = (wellFormed / rows.length) * 70;
    const activeRate = (active / rows.length) * 30;
    if (onScoreUpdate) onScoreUpdate(Math.round(completeness + activeRate));
  };

  const fetchRecords = async () => {
    try {
      const res = await axios.get(API.list, { headers });
      const rows = Array.isArray(res.data) ? res.data : [];
      setRecords(rows);
      calculateScore(rows);
    } catch (error) {
      console.error('Error fetching consent records:', error);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.dataSubject.trim())
      e.dataSubject = 'Enter the data subject (email or ID).';
    if (!form.purposes.length)
      e.purposes = 'Select at least one specific purpose.';
    if (!form.noticeVersion.trim())
      e.noticeVersion = 'Record which privacy notice version was shown.';
    if (form.sensitiveToggle && !form.explicitConsent)
      e.explicit = 'Sensitive personal data requires recorded explicit consent.';
    if (form.guardianToggle && !form.guardianConsent)
      e.guardian = 'Guardian consent is required for a minor.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = {
      dataSubject: form.dataSubject.trim(),
      purposes: form.purposes,
      legalBasis: form.legalBasis,
      status: form.status,
      collectionMethod: form.collectionMethod,
      noticeVersion: form.noticeVersion.trim(),
      sensitive: form.sensitiveToggle
        ? { explicitConsent: form.explicitConsent }
        : null,
      guardian: form.guardianToggle
        ? { guardianConsent: form.guardianConsent }
        : null,
      notes: form.notes.trim(),
      capturedAt: new Date().toISOString(),
    };
    setSubmitting(true);
    try {
      await axios.post(API.create, payload, { headers });
      await logActivity(
        `Recorded UAE PDPL consent for ${payload.dataSubject} (${form.legalBasis})`,
        'success'
      );
      showNotification('Consent record saved!');
      setForm(blankConsent);
      setErrors({});
      setShowModal(false);
      fetchRecords();
    } catch (error) {
      console.error('Error saving consent:', error);
      showNotification('Error saving consent record', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawConsent = async (id, subject) => {
    if (!window.confirm(`Withdraw consent for ${subject}?`)) return;
    try {
      await axios.put(
        API.update(id),
        { status: 'Withdrawn', withdrawnAt: new Date().toISOString() },
        { headers }
      );
      await logActivity(`Withdrew UAE PDPL consent for ${subject}`, 'warning');
      showNotification('Consent withdrawn.');
      fetchRecords();
    } catch (error) {
      console.error('Error withdrawing consent:', error);
      showNotification('Error withdrawing consent', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this consent record?')) return;
    try {
      await axios.delete(API.remove(id), { headers });
      await logActivity('Deleted UAE PDPL consent record', 'warning');
      showNotification('Record deleted.');
      fetchRecords();
    } catch (error) {
      console.error('Error deleting record:', error);
      showNotification('Error deleting record', 'danger');
    }
  };

  const purposesText = (p) => (Array.isArray(p) ? p.join(', ') : p || '—');

  return (
    <>
      <Alert variant="info" className="d-flex">
        <FaInfoCircle className="me-2 mt-1 flex-shrink-0" />
        <div>
          <strong>UAE PDPL consent</strong> (Federal Decree-Law 45/2021) must be
          clear, specific and unambiguous, withdrawable at any time, and
          provable by the controller. Sensitive personal data needs explicit
          consent.
        </div>
      </Alert>

      <Row className="mb-3">
        <Col>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            <FaPlus /> Record Consent
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Data Subject</th>
            <th>Purposes</th>
            <th>Legal Basis</th>
            <th>Collected Via</th>
            <th>Notice</th>
            <th>Sensitive</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted">
                No consent records yet.
              </td>
            </tr>
          ) : (
            records.map((r) => (
              <tr key={r.id || r.consentId}>
                <td>{r.dataSubject}</td>
                <td className="small">{purposesText(r.purposes)}</td>
                <td className="small">{r.legalBasis}</td>
                <td className="small">{r.collectionMethod}</td>
                <td className="small">{r.noticeVersion}</td>
                <td>
                  {r.sensitive ? (
                    <Badge bg={r.sensitive.explicitConsent ? 'success' : 'danger'}>
                      {r.sensitive.explicitConsent ? 'Explicit' : 'Missing'}
                    </Badge>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  <Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>
                    {r.status}
                  </Badge>
                </td>
                <td>
                  {r.status === 'Given' && (
                    <Button
                      size="sm"
                      variant="outline-warning"
                      className="me-1"
                      title="Withdraw consent"
                      onClick={() =>
                        withdrawConsent(r.id || r.consentId, r.dataSubject)
                      }
                    >
                      <FaBan />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(r.id || r.consentId)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Record Consent modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record UAE PDPL Consent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Data subject <span className="text-primary">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.dataSubject}
                isInvalid={!!errors.dataSubject}
                placeholder="Email or subject ID"
                onChange={(e) => set('dataSubject', e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {errors.dataSubject}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Purposes <span className="text-primary">*</span>{' '}
                <span className="text-muted small">(specific — pick each that applies)</span>
              </Form.Label>
              <Row>
                {PURPOSES.map((p) => (
                  <Col md={6} key={p}>
                    <Form.Check
                      type="checkbox"
                      id={`uae-purpose-${p}`}
                      label={p}
                      checked={form.purposes.includes(p)}
                      onChange={() => togglePurpose(p)}
                    />
                  </Col>
                ))}
              </Row>
              {errors.purposes && (
                <div className="text-danger small mt-1">{errors.purposes}</div>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Legal basis</Form.Label>
                  <Form.Select
                    value={form.legalBasis}
                    onChange={(e) => set('legalBasis', e.target.value)}
                  >
                    {LEGAL_BASES.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Collected via</Form.Label>
                  <Form.Select
                    value={form.collectionMethod}
                    onChange={(e) => set('collectionMethod', e.target.value)}
                  >
                    {COLLECTION_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Privacy notice version <span className="text-primary">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.noticeVersion}
                    isInvalid={!!errors.noticeVersion}
                    placeholder="e.g., Privacy Notice v2.0 (2026-01)"
                    onChange={(e) => set('noticeVersion', e.target.value)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.noticeVersion}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                  >
                    <option value="Given">Given</option>
                    <option value="Pending">Pending</option>
                    <option value="Withdrawn">Withdrawn</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div style={{ marginBottom: '8px' }}>
              <Form.Check
                type="checkbox"
                id="uaeSensitive"
                checked={form.sensitiveToggle}
                onChange={(e) => set('sensitiveToggle', e.target.checked)}
                label="Involves sensitive personal data"
              />
              <div style={subTextStyle}>
                Health, biometric, genetic, religious, racial, etc. — needs
                explicit consent under the PDPL.
              </div>
            </div>
            {form.sensitiveToggle && (
              <div className="border rounded p-3 mb-2">
                <Form.Check
                  type="checkbox"
                  id="uaeExplicit"
                  checked={form.explicitConsent}
                  onChange={(e) => set('explicitConsent', e.target.checked)}
                  label="Explicit consent obtained and recorded for sensitive data"
                />
                {errors.explicit && (
                  <div className="text-danger small mt-1">{errors.explicit}</div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '8px' }}>
              <Form.Check
                type="checkbox"
                id="uaeGuardian"
                checked={form.guardianToggle}
                onChange={(e) => set('guardianToggle', e.target.checked)}
                label="Data subject is a minor (guardian consent required)"
              />
            </div>
            {form.guardianToggle && (
              <div className="border rounded p-3 mb-2">
                <Form.Check
                  type="checkbox"
                  id="uaeGuardianConsent"
                  checked={form.guardianConsent}
                  onChange={(e) => set('guardianConsent', e.target.checked)}
                  label="Guardian consent obtained and verifiable"
                />
                {errors.guardian && (
                  <div className="text-danger small mt-1">{errors.guardian}</div>
                )}
              </div>
            )}

            <Form.Group className="mb-1" style={{ marginTop: '16px' }}>
              <Form.Label>Notes / evidence reference</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.notes}
                placeholder="e.g., signed form link, opt-in event ID, IP/timestamp"
                onChange={(e) => set('notes', e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save Consent'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UaeConsentManagementTab;